import { z } from 'zod';
import type { Membership } from './domain.js';
import { Asset, Org, Reel, ReelEvent, Script, Source, type Metrics } from './domain.js';
import { ChatMessage } from './chat.js';
import { ScriptOp } from './script-ops.js';
import {
  Component,
  type Decision,
  type Experiment,
  type PlaybookVersion,
  type ReelOutcome,
  type Report,
} from './learning.js';
import type { ErrorSheetRow, StepStatsRow } from './error-analysis.js';
export type { StepStatsRow };
import type { ScriptDiff } from './objective.js';

/**
 * HTTP contract between apps/web and services/api. All routes live under `/v1` and need a Cognito
 * access token (`Authorization: Bearer <token>`), except `GET /v1/health` and `GET /v1/zernio/callback`.
 * Errors: `{ error: { code, message } }` with codes below. Bodies and responses are JSON.
 */

export const ErrorCode = z.enum([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'stage',
  'internal',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;
export interface ApiError {
  error: { code: ErrorCode; message: string; details?: unknown };
}

// ---- requests ----
export const UpdateOrgBody = z.object({
  name: z.string().min(1).max(120).optional(),
  instagramHandle: Org.shape.instagramHandle,
  website: z.string().max(300).optional(),
});
export const CreateReelBody = z.object({ source: Source, assets: z.array(Asset).max(20).default([]) });
export const UploadBody = z.object({
  kind: z.enum(['asset', 'take']),
  contentType: z.string().regex(/^(image|video|audio)\/[\w.+-]+$/),
  name: z.string().max(200).default('upload'),
  /** Required for takes: the reel the recording belongs to. */
  reelId: z.string().optional(),
});
export const PatchScriptBody = z.object({
  baseVersion: z.number().int().nonnegative(),
  ops: z.array(ScriptOp).min(1).max(50),
});
export const ChatBody = z.object({ message: z.string().min(1).max(2000) });
export const UndoBody = z.object({ messageId: z.string() });
/** Finalizes an upload into a take (review F09): the API checks the object's size, type and ETag. */
export const AddTakeBody = z.object({
  uploadId: z.string().min(1),
  durationMs: z
    .number()
    .int()
    .nonnegative()
    .max(10 * 60 * 1000),
});
export const SelectTakeBody = z.object({ n: z.number().int().positive() });
export const CaptionBody = z.object({ caption: z.string().max(2200) });

// ---- responses ----
export interface MeResponse {
  userId: string;
  email?: string;
  /** Member of the BeatSquares staff org (env STAFF_ORG_ID). Only staff see the Learning area. */
  staff: boolean;
  memberships: Membership[];
  orgs: Org[];
}
/**
 * Presigned S3 POST (expires after 15 minutes). Send `multipart/form-data` to `url` with every entry of `fields`
 * first and the file last, as the form field `file`. S3 enforces the content type and the size limit
 * (assets 15 MB, takes 500 MB).
 */
export interface UploadResponse {
  /** Random upload id; pass it to addTake (takes) or with the asset in createReel. */
  uploadId: string;
  key: string;
  url: string;
  method: 'POST';
  fields: Record<string, string>;
}
export interface ReelDetail {
  reel: Reel;
  script?: Script;
  /** Presigned GET for the rendered MP4, when there is one. */
  videoUrl?: string;
  /** Presigned GET per take number, for playback. */
  takeUrls?: Record<number, string>;
  /** Viewable URL per collected image key of the script (presigned copy, or the stock hotlink). */
  imageUrls?: Record<string, string>;
  stale: boolean;
  /** Why the reel can or cannot be published now (review F04). */
  eligibility?: { ok: boolean; reasons: Array<{ code: string; message: string }> };
}

/** Optional: a stable id per user action, so a retried or doubled request returns the same intent (R01). */
export const PublishBody = z.object({ requestId: z.string().min(8).max(100).optional() }).default({});
export const OverrideBody = z.object({ reason: z.string().min(10).max(1000) });
export const ResolvePublicationBody = z.object({
  outcome: z.enum(['published', 'not_published']),
  url: z.string().url().optional(),
  note: z.string().min(5).max(1000),
});
export interface ScriptResponse {
  script: Script;
  changed: string[];
}
/**
 * Assistant edits that touch fields a person changed while the assistant was thinking (review F08).
 * They are not applied; the person applies them (patchScript with `expected` values) or keeps their own.
 */
export interface ChatProposal {
  baseVersion: number;
  ops: ScriptOp[];
  conflictFields: string[];
}
export interface ChatResponse {
  messages: ChatMessage[]; // the user message and the assistant reply
  script: Script;
  proposal?: ChatProposal;
}
export interface EventsResponse {
  events: ReelEvent[];
}
export interface MetricsResponse {
  metrics?: Metrics;
}

export const LabelBody = z.object({
  component: Component,
  verdict: z.enum(['ok', 'error', 'upstream']),
  note: z.string().max(1000).default(''),
});

export interface ErrorSheetResponse {
  /** Unsatisfactory reels in the window: objective below 0, or edit ratio above 0.25, or fidelity failed. */
  reels: number;
  rows: ErrorSheetRow[];
}

/** One reel prepared for the weekly human error analysis. */
export interface ReviewItem {
  reelId: string;
  title: string;
  orgHandle?: string;
  postedAt?: string;
  why: string; // e.g. "objective -0.8, editors changed 41 %"
  objective?: number;
  editRatio?: number;
  /** Automatic attribution, for the person to confirm or correct. */
  suspected: Component[];
  /** Per pipeline step: a short summary of input and output from the traces. */
  steps: Array<{ component: Component; summary: string; latencyMs?: number }>;
  diff?: Pick<ScriptDiff, 'charsChanged' | 'editRatio' | 'edits'>;
  labels: Array<{ component: Component; verdict: 'ok' | 'error' | 'upstream'; note: string }>;
}

export const DecideBody = z.object({ decision: z.enum(['approve', 'reject']), note: z.string().max(1000).optional() });
export const RollbackBody = z.object({
  toVersion: z.number().int().positive(),
  note: z.string().max(1000),
  /** "global" or an org id. */
  scope: z.string().min(1).max(128).default('global'),
});

export interface LearningOverview {
  activeVersion?: PlaybookVersion;
  latestReport?: Omit<Report, 'body'>;
  openDecisions: number;
  runningExperiments: Experiment[];
  /** Last 28 days, posted reels with day-7 metrics. */
  recent: { reels: number; meanObjective?: number; meanEditRatio?: number; fidelityPassRate?: number };
  outcomes: ReelOutcome[];
}
export type { Decision, Experiment, PlaybookVersion, Report };

/** Route table: method, path, body schema. The API and the web client both import it. */
export const ROUTES = {
  health: { method: 'GET', path: '/v1/health' },
  me: { method: 'GET', path: '/v1/me' },
  getOrg: { method: 'GET', path: '/v1/orgs/:orgId' },
  updateOrg: { method: 'PATCH', path: '/v1/orgs/:orgId', body: UpdateOrgBody },
  readChannel: { method: 'POST', path: '/v1/orgs/:orgId/channel/read' },
  connectZernio: { method: 'POST', path: '/v1/orgs/:orgId/zernio/connect' }, // -> { url }
  zernioCallback: { method: 'GET', path: '/v1/zernio/callback' }, // public, redirects to the app
  listReels: { method: 'GET', path: '/v1/orgs/:orgId/reels' }, // -> { reels }
  createReel: { method: 'POST', path: '/v1/orgs/:orgId/reels', body: CreateReelBody }, // -> ReelDetail
  upload: { method: 'POST', path: '/v1/orgs/:orgId/uploads', body: UploadBody }, // -> UploadResponse
  getReel: { method: 'GET', path: '/v1/reels/:reelId' }, // -> ReelDetail
  patchScript: { method: 'PATCH', path: '/v1/reels/:reelId/script', body: PatchScriptBody }, // -> ScriptResponse
  regenerate: { method: 'POST', path: '/v1/reels/:reelId/regenerate' }, // -> ReelDetail
  addImages: { method: 'POST', path: '/v1/reels/:reelId/images' }, // collects and places images -> ReelDetail
  // stops the running script, transcribe or render job; posting cannot be cancelled
  cancelJob: { method: 'POST', path: '/v1/reels/:reelId/cancel' }, // -> ReelDetail
  listChat: { method: 'GET', path: '/v1/reels/:reelId/chat' }, // -> { messages }
  chat: { method: 'POST', path: '/v1/reels/:reelId/chat', body: ChatBody }, // -> ChatResponse
  undo: { method: 'POST', path: '/v1/reels/:reelId/chat/undo', body: UndoBody }, // -> ScriptResponse
  approve: { method: 'POST', path: '/v1/reels/:reelId/approve' }, // -> ReelDetail
  addTake: { method: 'POST', path: '/v1/reels/:reelId/takes', body: AddTakeBody }, // -> ReelDetail
  selectTake: { method: 'PUT', path: '/v1/reels/:reelId/take', body: SelectTakeBody }, // -> ReelDetail
  render: { method: 'POST', path: '/v1/reels/:reelId/render' }, // -> ReelDetail (job running)
  writePost: { method: 'POST', path: '/v1/reels/:reelId/post' }, // generates caption if empty -> ReelDetail
  setCaption: { method: 'PUT', path: '/v1/reels/:reelId/caption', body: CaptionBody }, // -> ReelDetail
  publish: { method: 'POST', path: '/v1/reels/:reelId/publish', body: PublishBody }, // -> ReelDetail (publication intent created; 202)
  // staff only: accept reviewable video warnings with a reason (never factual failures)
  overrideQuality: { method: 'POST', path: '/v1/reels/:reelId/quality/override', body: OverrideBody }, // -> ReelDetail
  recheckQuality: { method: 'POST', path: '/v1/reels/:reelId/quality/recheck' }, // -> ReelDetail (job running)
  // staff only: resolve a publication stuck in "unknown" after checking Instagram by hand
  resolvePublication: { method: 'POST', path: '/v1/reels/:reelId/publication/resolve', body: ResolvePublicationBody }, // -> ReelDetail
  events: { method: 'GET', path: '/v1/reels/:reelId/events' }, // -> EventsResponse
  // ---- learning (improvement loop). Admins of any org may read; deciding and rolling back needs role admin. ----
  learningOverview: { method: 'GET', path: '/v1/learning' }, // -> LearningOverview
  listReports: { method: 'GET', path: '/v1/learning/reports' }, // -> { reports } (without body)
  getReport: { method: 'GET', path: '/v1/learning/reports/:reportId' }, // -> { report }
  listDecisions: { method: 'GET', path: '/v1/learning/decisions' }, // -> { decisions }
  decide: { method: 'POST', path: '/v1/learning/decisions/:decisionId', body: DecideBody }, // -> { decision }
  listExperiments: { method: 'GET', path: '/v1/learning/experiments' }, // -> { experiments }
  stopExperiment: { method: 'POST', path: '/v1/learning/experiments/:experimentId/stop' }, // -> { experiment }
  listPlaybook: { method: 'GET', path: '/v1/learning/playbook' }, // -> { versions } (scope global)
  rollbackPlaybook: { method: 'POST', path: '/v1/learning/playbook/rollback', body: RollbackBody }, // -> { version }
  // ---- Ng: error analysis, step stats, weekly review (staff only) ----
  errorSheet: { method: 'GET', path: '/v1/learning/errors' }, // ?days=28 -> ErrorSheetResponse
  stepStats: { method: 'GET', path: '/v1/learning/steps' }, // ?days=28 -> { rows: StepStatsRow[] }
  reviewQueue: { method: 'GET', path: '/v1/learning/review' }, // -> { items: ReviewItem[] } (5 worst unlabelled reels)
  labelStep: { method: 'POST', path: '/v1/learning/review/:reelId/labels', body: LabelBody }, // -> { label }
} as const;

export type RouteName = keyof typeof ROUTES;

export function buildPath(path: string, params: Record<string, string>): string {
  return path.replace(/:(\w+)/g, (_, k: string) => {
    const v = params[k];
    if (v === undefined) throw new Error(`Missing path parameter ${k}`);
    return encodeURIComponent(v);
  });
}

export { ChatMessage, Reel, Script, ReelEvent };
